import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Project, projectsApi } from "../lib/api/projects";

export default function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "ACTIVE" | "ARCHIVED" | "ALL"
  >("ACTIVE");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = {
          search: searchQuery || undefined,
          status: statusFilter === "ALL" ? undefined : statusFilter,
          limit: ITEMS_PER_PAGE,
          offset: (page - 1) * ITEMS_PER_PAGE,
        };
        const { projects: fetchedProjects, total: totalProjects } =
          await projectsApi.list(params);
        setProjects(fetchedProjects);
        setTotal(totalProjects);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch projects",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [searchQuery, statusFilter, page]);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Projects</h1>
        <Link
          to="/projects/new"
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
        >
          Create Project
        </Link>
      </div>

      <div className="mb-6 flex gap-4">
        <input
          type="text"
          placeholder="Search projects..."
          className="px-4 py-2 border rounded flex-grow"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="px-4 py-2 border rounded"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as "ACTIVE" | "ARCHIVED" | "ALL")
          }
        >
          <option value="ALL">All Projects</option>
          <option value="ACTIVE">Active</option>
          <option value="ARCHIVED">Archived</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading projects...</div>
      ) : error ? (
        <div className="text-red-500 text-center py-8">{error}</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No projects found.{" "}
          <Link to="/projects/new" className="text-blue-500 hover:underline">
            Create your first project
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <Link
              key={project._id}
              to={`/projects/${project._id}`}
              className="block p-6 border rounded-lg hover:border-blue-500 transition-colors"
            >
              <div className="flex justify-between items-start mb-2">
                <h2 className="text-xl font-semibold">{project.name}</h2>
                <span
                  className={`px-2 py-1 text-sm rounded ${
                    project.status === "ACTIVE"
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {project.status}
                </span>
              </div>
              <div className="text-sm text-gray-500 mb-2">{project.key}</div>
              {project.description && (
                <p className="text-gray-600 mb-4 line-clamp-2">
                  {project.description}
                </p>
              )}
              <div className="text-sm text-gray-500">
                Created{" "}
                {new Date(project.createdAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && total > ITEMS_PER_PAGE && (
        <div className="mt-8 flex justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2">
            Page {page} of {Math.ceil(total / ITEMS_PER_PAGE)}
          </span>
          <button
            onClick={() =>
              setPage((p) => Math.min(Math.ceil(total / ITEMS_PER_PAGE), p + 1))
            }
            disabled={page >= Math.ceil(total / ITEMS_PER_PAGE)}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
